// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useCallback } from 'react';
import { getConfigSettings } from '@api/portalServices';
import type { AuthenticationStrengthGetResponse, Setting } from '@api/types';
import { useEmitOnError } from '@errorHandler/context/useEmitOnError';
import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useAuthenticationStrengthQuery } from '@portal/containers/security/useAuthenticationStrength';
import {
  computeNewSettingValues,
  useUpdateConfigSettings,
} from '@portal/containers/security/useConfigSettings';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountRecoveryEnabledSettingValues,
  type AuthnStrengthValues,
  getSettingsTemplate,
  InstanceKey,
  SettingsValues,
} from '@portal/containers/security/constants';

export function findMfaTypeSetting(settings?: Setting[]): Setting | undefined {
  return settings?.find(item => item.name === SettingsValues.name);
}

export function findAccountRecoveryEnabledSetting(settings?: Setting[]): Setting | undefined {
  return settings?.find(item => item.name === AccountRecoveryEnabledSettingValues.name);
}

export const useSecurityData = () => {
  const queryClient = useQueryClient();

  const {
    data: ussAuthnStrength,
    isFetching: isFetchingUssAuthnStrength,
    error: ussAuthnStrengthError,
  } = useAuthenticationStrengthQuery(InstanceKey.USS);
  const {
    data: iamAuthnStrength,
    isFetching: isFetchingIamAuthnStrength,
    error: iamAuthnStrengthError,
  } = useAuthenticationStrengthQuery(InstanceKey.IAM);

  // Each read resolves to null for an absent setting: react-query rejects undefined data, which would
  // report a tenant that never stored the setting as a failed read.
  const {
    data: ussSetting,
    isFetching: isFetchingUSS,
    error: ussError,
    refetch: refetchUSS,
  } = useQuery<Setting | null, ApiError>({
    queryKey: [QueryKey.CONFIG_SETTINGS, InstanceKey.USS],
    queryFn: async () => {
      const response = await getConfigSettings({
        ...getSettingsTemplate,
        instanceKey: InstanceKey.USS,
      });
      return findMfaTypeSetting(response?.settings) ?? null;
    },
    retry: false,
    staleTime: 0,
  });

  const {
    data: iamSetting,
    isFetching: isFetchingIAM,
    error: iamError,
    refetch: refetchIAM,
  } = useQuery<Setting | null, ApiError>({
    queryKey: [QueryKey.CONFIG_SETTINGS, InstanceKey.IAM],
    queryFn: async () => {
      const response = await getConfigSettings({
        ...getSettingsTemplate,
        instanceKey: InstanceKey.IAM,
      });
      return findMfaTypeSetting(response?.settings) ?? null;
    },
    retry: false,
    staleTime: 0,
  });

  const {
    data: accountRecoveryEnabledSetting,
    isFetching: isFetchingAccountRecoveryEnabled,
    error: accountRecoveryEnabledError,
    refetch: refetchAccountRecoveryEnabled,
  } = useQuery<Setting | null, ApiError>({
    queryKey: [QueryKey.CONFIG_SETTINGS, AccountRecoveryEnabledSettingValues.name],
    queryFn: async () => {
      // account-recovery-enabled is queried without an instanceKey.
      const response = await getConfigSettings(getSettingsTemplate);
      return findAccountRecoveryEnabledSetting(response?.settings) ?? null;
    },
    retry: false,
    staleTime: 0,
  });

  const { mutateAsync: updateSettings, isPending: isUpdating } = useUpdateConfigSettings();

  useEmitOnError(ussError);
  useEmitOnError(iamError);
  useEmitOnError(ussAuthnStrengthError);
  useEmitOnError(iamAuthnStrengthError);
  useEmitOnError(accountRecoveryEnabledError);

  const refetchAll = useCallback(async () => {
    await Promise.all([refetchUSS(), refetchIAM(), refetchAccountRecoveryEnabled()]);
  }, [refetchUSS, refetchIAM, refetchAccountRecoveryEnabled]);

  // Sync the authentication-strength caches with the values just saved.
  // Trusts the BFF to store the sent value verbatim (no normalization).
  const syncAuthnStrengthCache = useCallback(
    (values: AuthnStrengthValues) => {
      const { ussNewSettingValue, iamNewSettingValue } = computeNewSettingValues(values);
      queryClient.setQueryData<AuthenticationStrengthGetResponse | undefined>(
        [QueryKey.AUTHENTICATION_STRENGTH, InstanceKey.USS],
        prev => (prev ? { ...prev, currentValue: ussNewSettingValue } : prev),
      );
      queryClient.setQueryData<AuthenticationStrengthGetResponse | undefined>(
        [QueryKey.AUTHENTICATION_STRENGTH, InstanceKey.IAM],
        prev => (prev ? { ...prev, currentValue: iamNewSettingValue } : prev),
      );
    },
    [queryClient],
  );

  const isLoading =
    isFetchingUSS ||
    isFetchingIAM ||
    isFetchingUssAuthnStrength ||
    isFetchingIamAuthnStrength ||
    isFetchingAccountRecoveryEnabled ||
    isUpdating;

  return {
    ussAuthnStrength,
    iamAuthnStrength,
    ussAuthnStrengthError,
    iamAuthnStrengthError,
    ussSetting: ussSetting ?? undefined,
    iamSetting: iamSetting ?? undefined,
    ussError,
    iamError,
    accountRecoveryEnabledSetting: accountRecoveryEnabledSetting ?? undefined,
    accountRecoveryEnabledError,
    isLoading,
    refetchAll,
    syncAuthnStrengthCache,
    updateSettings,
  };
};
